/**
 * Słownik surowców.
 *
 * One dictionary on the server, `materials.json`, keyed `MAT_…`, and everything
 * that has to name a material reads it — this client, the admin panel, the
 * prices in the catalogue. Nothing here is written down twice.
 *
 * The Polish words are **placeholders**. They will move to translations keyed by
 * the material; the key is the part that will not move, which is why it is the
 * key and not the name.
 */

/** A material key, as everything spells it: `MAT_LOGS`, `MAT_STONES`. */
export type Material = string;

export interface MaterialInfo {
  /** What it is called on its own. Temporary — see above. */
  label: string;
  /** The three Polish forms a count needs: 1 kłoda, 2 kłody, 5 kłód. */
  forms: [string, string, string];
  /** What to draw beside a number. An emoji until there are icon files. */
  icon: string;
  /** For a swatch or a bar, as CSS writes it. */
  color: string;
}

export interface Materials {
  version: number;
  materials: Record<Material, MaterialInfo>;
}

export const MATERIALS_VERSION = 1;

export async function loadMaterials(api: string): Promise<Materials> {
  const response = await fetch(`${api}/api/world/materials`);
  if (!response.ok) {
    throw new Error(`słownik surowców: ${response.status}`);
  }

  const dictionary = (await response.json()) as Materials;
  if (dictionary.version !== MATERIALS_VERSION) {
    throw new Error(
      `słownik surowców w wersji ${dictionary.version}, klient czyta ${MATERIALS_VERSION}`
    );
  }
  return dictionary;
}

/**
 * A count with the right word after it.
 *
 * A material the dictionary does not know still prints — with its key for a
 * name. Losing an amount because nobody wrote a word for it would be the worse
 * failure of the two.
 */
export function countOf(dictionary: Materials, material: Material, amount: number): string {
  return `${amount} ${formOf(dictionary, material, amount)}`;
}

/**
 * The word a count takes, without the count.
 *
 * Split out of `countOf` for a rate: "2,6 kłody na godzinę" needs the form for
 * a number it does not print beside it. Falls back to the key for the same
 * reason `countOf` does — a material nobody wrote a word for still has to name
 * itself.
 */
export function formOf(dictionary: Materials, material: Material, amount: number): string {
  const info = dictionary.materials[material];
  if (!info) return material;

  const rest = amount % 10;
  const teen = amount % 100 >= 12 && amount % 100 <= 14;
  return amount === 1
    ? info.forms[0]
    : rest >= 2 && rest <= 4 && !teen
      ? info.forms[1]
      : info.forms[2];
}

export function iconOf(dictionary: Materials, material: Material): string {
  return dictionary.materials[material]?.icon ?? '·';
}

export function labelOf(dictionary: Materials, material: Material): string {
  return dictionary.materials[material]?.label ?? material;
}
